"use client";

import { useMemo } from "react";
import { Button } from "@ui/components/core/Button";
import { useTranslations } from "next-intl";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import {
  string,
  pipe,
  trim,
  nonEmpty,
  length,
  regex,
  safeParse,
  object,
  boolean,
  number,
  minValue,
  optional,
  nullable,
} from "valibot";
import { HeroAgentCardLayout, ResendButton, ResizableProgressBar } from "./Components";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@ui/components/core/InputOTP";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { useMutation } from "@ui/hooks/useMutation";
import { getCaptchaToken } from "@/app/_providers/hcaptcha/core";
import { toast } from "@/app/_providers/toast";
import { env } from "@/env";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { getErrorMessageFromUnknownError } from "@utilities/errors";

// Form types
type VerifyPhoneFormValues = {
  verification_code: string;
};

// Create form context and hooks
const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {},
  formComponents: {},
});

export interface VerifyPhoneViewProps {
  onSubmit: (code: string) => void;
  onBack: () => void;
  /** Callback to resend the verification code */
  onResend: () => void;
  /** Agent ID for the verification request */
  agentId: string;
  /** Phone number to verify (from previous step) */
  phoneNumber: string;
  currentStep?: number;
  totalSteps?: number;
}

const responseSchema = object({
  success: boolean(),
  remaining_attempts: optional(nullable(pipe(number(), minValue(0)))),
});

export function VerifyPhoneView({
  onSubmit,
  onBack,
  onResend,
  agentId,
  phoneNumber,
  currentStep = 1,
  totalSteps = 3,
}: VerifyPhoneViewProps) {
  const t = useTranslations("ClientKeys.hero_agent.views.verify_phone");
  const tValidation = useTranslations("ClientKeys.forms.validation");

  // Mutation for verifying the OTP
  const verifyMutation = useMutation({
    mutationFn: async (variables: { otp: string }) => {
      const hcaptchaToken = await getCaptchaToken({ behaviourType: "agent_verify_phone" });

      const response = await fetch(`${env.NEXT_PUBLIC_XI_SERVER_URL}/v1/agents/request-call/verify/anonymous`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          otp: variables.otp,
          phone_number: phoneNumber,
          agent_id: agentId,
          hcaptcha_token: hcaptchaToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = getErrorMessageFromUnknownError(errorData, {
          defaultMessage: tValidation("default"),
        });
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const result = safeParse(responseSchema, data);
      if (!result.success) {
        throw new Error(tValidation("default"));
      }
      if (!result.output.success) {
        const attempts = result.output.remaining_attempts ?? 5;
        throw new Error(t("errors.invaid_verification_code", { attempts: attempts.toString() })); // Failed verification, got x remaining attempts
      }

      return result.output;
    },
    onError: error => {
      toast.error(error.message || tValidation("default"));
    },
  });

  // Memoized Valibot schema for validation
  const codeSchema = useMemo(
    () =>
      pipe(
        string(),
        trim(),
        nonEmpty(tValidation("required")),
        length(6, tValidation("exact_length", { length: 6 })),
        regex(/^\d{6}$/, tValidation("digits_only")),
      ),
    [tValidation],
  );

  const form = useAppForm({
    defaultValues: {
      verification_code: "",
    } satisfies VerifyPhoneFormValues,
    onSubmit: async ({ value }) => {
      captureEvent(EventName.mw_hero_agent_verify_submitted, { agent_id: agentId });
      try {
        const response = await verifyMutation.mutateAsync({ otp: value.verification_code });
        captureEvent(EventName.mw_hero_agent_verify_succeeded, { agent_id: agentId });
        // Only proceed to next step if verification succeeded
        onSubmit(value.verification_code);
      } catch (error) {
        captureEvent(EventName.mw_hero_agent_verify_failed, {
          agent_id: agentId,
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
        // Error is already handled by mutation's onError
      }
    },
  });

  return (
    <HeroAgentCardLayout width='full' className='tw-h-full tw-flex tw-flex-col tw-overflow-x-hidden tw-py-2.5'>
      {/* Header */}
      <div className='tw-flex tw-flex-col tw-max-w-sm'>
        <h3 className='tw-mb-4 tw-type-2xl'>{t("title")}</h3>
        <p className='tw-text-gray-500 tw-mb-8 tw-type-xs'>{t("subtitle")}</p>
      </div>

      <form
        className='tw-flex-1 tw-flex tw-flex-col'
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        {/* Verification Code Field */}
        <div className='tw-mb-6 sm:tw-mb-4'>
          <label className='tw-block tw-mb-3 tw-type-xs tw-font-medium'>{t("code_label")}</label>
          <form.Field
            name='verification_code'
            validators={{
              onBlur: ({ value }) => {
                const result = safeParse(codeSchema, value);
                return result.success ? undefined : result.issues[0]?.message;
              },
            }}
          >
            {field => (
              <form.Subscribe selector={state => ({ isSubmitting: state.isSubmitting })}>
                {({ isSubmitting }) => (
                  <InputOTP
                    maxLength={6}
                    value={field.state.value}
                    onChange={(value: string) => {
                      field.handleChange(value);
                      // Auto-submit when all 6 digits are entered
                      if (!isSubmitting && value.length === 6 && safeParse(codeSchema, value).success) {
                        void form.handleSubmit();
                      }
                    }}
                    onBlur={field.handleBlur}
                    slotSize='lg'
                  >
                    <InputOTPGroup className='tw-gap-1 tw-flex-wrap'>
                      <InputOTPSlot index={0} size='lg' />
                      <InputOTPSlot index={1} size='lg' />
                      <InputOTPSlot index={2} size='lg' />
                      <InputOTPSlot index={3} size='lg' />
                      <InputOTPSlot index={4} size='lg' />
                      <InputOTPSlot index={5} size='lg' />
                    </InputOTPGroup>
                  </InputOTP>
                )}
              </form.Subscribe>
            )}
          </form.Field>
        </div>

        {/* Resend Link */}
        <p className='tw-text-gray-500 tw-type-xs tw-mb-8'>
          {t("didnt_get_code")}{" "}
          <ResendButton
            onResend={() => {
              captureEvent(EventName.mw_hero_agent_verify_resend_clicked, { agent_id: agentId });
              onResend();
            }}
            initialAttempts={1}
            enabledContent={() => <span className='tw-font-semibold tw-text-black'>{t("resend_now")}</span>}
            disabledContent={({ cooldown, attempts, maxAttempts }) => (
              <span className='tw-text-gray-400'>
                {attempts >= maxAttempts ? t("max_attempts_reached") : t("resend_in", { seconds: cooldown })}
              </span>
            )}
          />
        </p>

        {/* Spacer */}
        <div className='tw-flex-1' />

        {/* Footer with actions and progress */}
        <div className='tw-flex tw-items-center tw-justify-between tw-mt-auto tw-py-1.5 tw-gap-4'>
          <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
            {({ canSubmit, isSubmitting }) => (
              <>
                {/* Left side - Back button and Get a call */}
                <div className='tw-flex tw-items-center tw-gap-3'>
                  <Button
                    type='button'
                    variant='elevated'
                    size='pill-lg'
                    onClick={onBack}
                    icon={<ArrowLeft className='tw-size-5' />}
                    aria-label={t("back")}
                    disabled={isSubmitting}
                  />
                  <Button
                    type='submit'
                    variant='primary'
                    size='md'
                    disabled={!canSubmit || isSubmitting}
                    icon={
                      isSubmitting ? (
                        <LoaderCircle className='tw-hidden tw-animate-spin sm:tw-block' size={14} />
                      ) : undefined
                    }
                  >
                    {t("get_call_button")}
                  </Button>
                </div>

                {/* Right side - Progress indicator */}
                <ResizableProgressBar currentStep={currentStep} totalSteps={totalSteps} />
              </>
            )}
          </form.Subscribe>
        </div>
      </form>
    </HeroAgentCardLayout>
  );
}
